import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Users,
  Music,
  Building,
  ShieldCheck,
  Banknote,
  Calendar,
  Ticket,
  AlertTriangle,
  Activity,
} from "lucide-react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { StatCard, SectionHeader, ProgressRow } from "@/components/admin/AdminUI";
import { LoadingState, ErrorState } from "@/components/dashboard/common/PageShell";
import { useAdminProfiles, useAdminOrders, useAdminCounts, toDailySeries } from "@/hooks/admin/useAdminMetrics";

const PIE_COLORS = ["hsl(var(--primary))", "#8b5cf6", "#06b6d4", "#f59e0b", "#ef4444"];

const AdminOverview = () => {
  const { data: profiles = [], isLoading, error, refetch } = useAdminProfiles();
  const { data: orders = [] } = useAdminOrders();
  const { data: counts } = useAdminCounts();

  const metrics = useMemo(() => {
    const byType = (t: string) => profiles.filter((p) => (p.user_type ?? "groover") === t).length;
    const paid = orders.filter((o) => o.status === "paid");
    const gmv = paid.reduce((s, o) => s + Number(o.total ?? 0), 0);
    const activeCutoff = Date.now() - 30 * 24 * 60 * 60 * 1000;
    const active = profiles.filter((p) => p.last_active_at && new Date(p.last_active_at).getTime() > activeCutoff).length;
    const verified = profiles.filter((p) => p.verification_status === "verified").length;
    const pendingVerify = profiles.filter((p) => p.verification_status === "pending").length;
    return {
      total: profiles.length,
      creators: byType("creator"),
      venues: byType("venue"),
      groovers: byType("groover"),
      agencies: byType("creator_management") + byType("venue_oversight"),
      gmv,
      paidOrders: paid.length,
      active,
      verified,
      pendingVerify,
    };
  }, [profiles, orders]);

  const signupSeries = useMemo(
    () =>
      toDailySeries(
        profiles.filter((p) => p.created_at).map((p) => ({ created_at: p.created_at as string })),
        30,
      ),
    [profiles],
  );

  const revenueSeries = useMemo(
    () => toDailySeries(orders.filter((o) => o.status === "paid"), 30, (o) => Number(o.total ?? 0)),
    [orders],
  );

  const roleSplit = useMemo(
    () =>
      [
        { name: "Groovers", value: metrics.groovers },
        { name: "Creators", value: metrics.creators },
        { name: "Venues", value: metrics.venues },
        { name: "Agencies", value: metrics.agencies },
      ].filter((s) => s.value > 0),
    [metrics],
  );

  const topCities = useMemo(() => {
    const map = new Map<string, number>();
    profiles.forEach((p) => {
      if (!p.city) return;
      map.set(p.city, (map.get(p.city) ?? 0) + 1);
    });
    return Array.from(map, ([city, count]) => ({ city, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 6);
  }, [profiles]);

  if (isLoading) return <LoadingState label="Loading platform metrics…" />;
  if (error) return <ErrorState message={(error as Error).message} onRetry={() => refetch()} />;

  return (
    <div className="space-y-8">
      <SectionHeader
        title="Platform health"
        description="Live governance snapshot across users, revenue and compliance."
        actions={
          <Badge variant="outline" className="gap-1 border-emerald-500/40 text-emerald-500">
            <Activity className="h-3 w-3" /> Live data
          </Badge>
        }
      />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard label="Total accounts" value={metrics.total} icon={Users} hint={`${metrics.active} active in 30 days`} />
        <StatCard label="Creators" value={metrics.creators} icon={Music} hint={`${metrics.venues} venues onboarded`} />
        <StatCard
          label="Paid GMV"
          value={`R${metrics.gmv.toLocaleString("en-ZA", { maximumFractionDigits: 0 })}`}
          icon={Banknote}
          tone="positive"
          hint={`${metrics.paidOrders} settled orders`}
        />
        <StatCard
          label="Verification queue"
          value={metrics.pendingVerify}
          icon={ShieldCheck}
          tone={metrics.pendingVerify > 0 ? "warning" : "positive"}
          hint={`${metrics.verified} verified accounts`}
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard label="Events" value={counts?.events ?? 0} icon={Calendar} />
        <StatCard label="Tickets issued" value={counts?.tickets ?? 0} icon={Ticket} />
        <StatCard label="Bookings" value={counts?.bookings ?? 0} icon={Building} />
        <StatCard
          label="Open payout requests"
          value={counts?.payout_requests ?? 0}
          icon={AlertTriangle}
          tone={(counts?.payout_requests ?? 0) > 0 ? "warning" : "default"}
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-base text-foreground">New accounts · 30 days</CardTitle>
          </CardHeader>
          <CardContent className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={signupSeries}>
                <defs>
                  <linearGradient id="signupFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="label" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} allowDecimals={false} />
                <Tooltip
                  contentStyle={{
                    background: "hsl(var(--card))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: 8,
                    color: "hsl(var(--foreground))",
                  }}
                />
                <Area type="monotone" dataKey="value" stroke="hsl(var(--primary))" fill="url(#signupFill)" name="Signups" />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-base text-foreground">Settled revenue · 30 days (ZAR)</CardTitle>
          </CardHeader>
          <CardContent className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={revenueSeries}>
                <defs>
                  <linearGradient id="revFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10b981" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="label" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} />
                <Tooltip
                  contentStyle={{
                    background: "hsl(var(--card))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: 8,
                    color: "hsl(var(--foreground))",
                  }}
                />
                <Area type="monotone" dataKey="value" stroke="#10b981" fill="url(#revFill)" name="Revenue" />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-base text-foreground">Account mix</CardTitle>
          </CardHeader>
          <CardContent className="h-64">
            {roleSplit.length === 0 ? (
              <p className="text-sm text-muted-foreground">No accounts yet.</p>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={roleSplit} dataKey="value" nameKey="name" innerRadius={45} outerRadius={80} paddingAngle={3}>
                    {roleSplit.map((entry, i) => (
                      <Cell key={entry.name} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Legend wrapperStyle={{ fontSize: 12, color: "hsl(var(--muted-foreground))" }} />
                  <Tooltip
                    contentStyle={{
                      background: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: 8,
                      color: "hsl(var(--foreground))",
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-base text-foreground">Top cities</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {topCities.length === 0 ? (
              <p className="text-sm text-muted-foreground">No location data captured yet.</p>
            ) : (
              topCities.map((c) => <ProgressRow key={c.city} label={c.city} value={c.count} total={metrics.total} />)
            )}
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-base text-foreground">Governance readiness</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <ProgressRow label="Verified accounts" value={metrics.verified} total={metrics.total || 1} />
            <ProgressRow
              label="Banking details captured"
              value={profiles.filter((p) => p.account_number).length}
              total={metrics.creators + metrics.venues || 1}
            />
            <ProgressRow
              label="Active in last 30 days"
              value={metrics.active}
              total={metrics.total || 1}
            />
            <ProgressRow label="Staff role assignments" value={counts?.user_roles ?? 0} total={metrics.total || 1} />
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminOverview;
